"use client";

import type { ReactNode } from "react";

/**
 * The pieces of Settings' structure (docs/ux/screens/ConsumerProfileScreen.md):
 * the labelled section every group of controls sits in, and the card a
 * section's form sits on.
 */

/**
 * One group of Settings controls under its own heading.
 *
 * From `md` up the heading sits in a column to the left of its cards, the
 * shape people know from every settings page they have used; below that it
 * sits above them. `tone="danger"` is for the last section only, and it is
 * the heading that turns red, never the page around it.
 */
export function SettingsSection({
  id,
  title,
  detail,
  tone = "default",
  children,
}: {
  id: string;
  title: string;
  detail: string;
  tone?: "default" | "danger";
  children: ReactNode;
}) {
  const headingId = `settings-section-${id}`;
  return (
    <section
      aria-labelledby={headingId}
      className="grid gap-3 md:grid-cols-[11rem_minmax(0,1fr)] md:gap-8"
    >
      <div className="md:pt-2">
        <h2
          id={headingId}
          className={`text-xs font-bold uppercase tracking-[0.12em] ${
            tone === "danger" ? "text-danger" : "text-accent"
          }`}
        >
          {title}
        </h2>
        <p className="mt-1 text-sm text-muted">{detail}</p>
      </div>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

/**
 * The surface a section's form sits on. Not `Card`, which centres itself at
 * `max-w-md` for single-column screens and has no way to be widened: Tailwind
 * does not decide between two `max-w-*` classes on one element by their order
 * in the attribute, so overriding it would work or not by accident.
 */
export function SettingsCard({
  tone = "default",
  children,
}: {
  tone?: "default" | "danger";
  children: ReactNode;
}) {
  return (
    <div
      className={`rounded-2xl border bg-surface p-6 shadow-[var(--shadow-card)] sm:p-8 ${
        tone === "danger" ? "border-danger/30" : "border-line"
      }`}
    >
      {children}
    </div>
  );
}
